import Phaser from 'phaser';
import { createCreatureGalleryEntries } from '@/dev/creature-gallery-samples';
import { deriveVisualEvolutionProfile } from '@/domain/care/visual-evolution';
import { buildCreatureRenderer, type CreatureRendererHandle } from '../render/CreatureRenderer';

const COLS = 5;
const CELL_W = 180;
const CELL_H = 146;

export class CreatureGalleryScene extends Phaser.Scene {
  static readonly KEY = 'CreatureGalleryScene';

  private handles: CreatureRendererHandle[] = [];

  constructor() {
    super({ key: CreatureGalleryScene.KEY });
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#101018');
    this.add
      .text(24, 18, 'Himotchi visual gallery', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#ffd6b3',
      })
      .setDepth(500);
    this.add
      .text(24, 42, 'Fixed genotypes rendered with the current procedural renderer', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#b8a890',
      })
      .setDepth(500);

    const entries = createCreatureGalleryEntries(1_000);
    const startX = 92;
    const startY = 126;
    const rows = Math.ceil(entries.length / COLS);
    const contentH = startY + rows * CELL_H + 48;
    const viewportH = this.scale.height;
    const maxScrollY = Math.max(0, contentH - viewportH);

    this.cameras.main.setBounds(0, 0, Math.max(this.scale.width, COLS * CELL_W + 40), Math.max(viewportH, contentH));
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, dy: number) => {
      this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY + dy, 0, maxScrollY);
    });

    this.input.keyboard?.on('keydown-UP', () => {
      this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY - 64, 0, maxScrollY);
    });
    this.input.keyboard?.on('keydown-DOWN', () => {
      this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY + 64, 0, maxScrollY);
    });
    this.input.keyboard?.on('keydown-PAGE_UP', () => {
      this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY - viewportH * 0.8, 0, maxScrollY);
    });
    this.input.keyboard?.on('keydown-PAGE_DOWN', () => {
      this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY + viewportH * 0.8, 0, maxScrollY);
    });

    entries.forEach((entry, index) => {
      const { creature } = entry;
      const visual = deriveVisualEvolutionProfile(creature);
      const col = index % COLS;
      const row = Math.floor(index / COLS);
      const x = startX + col * CELL_W;
      const y = startY + row * CELL_H;
      const cellTop = y - 56;

      if (col === 0) {
        this.add
          .text(18, y - 6, entry.rowLabel, {
            fontFamily: 'monospace',
            fontSize: '11px',
            color: '#b8a890',
          })
          .setDepth(500);
      }

      this.add
        .rectangle(x, y + 16, CELL_W - 22, CELL_H - 10, 0x171926, 0.86)
        .setStrokeStyle(1, 0x3a3f5c, 0.9)
        .setDepth(-10);

      const handle = buildCreatureRenderer(this, creature, {
        ...(entry.previewExpression ? { expressionOverride: entry.previewExpression } : {}),
        ...(entry.previewPose ? { poseOverride: entry.previewPose } : {}),
        reducedMotion: entry.reducedMotion ?? !entry.animate,
        scale: 0.75,
        showLabels: false,
        sleepMode: 'awake',
      });
      handle.container.setPosition(x, y);
      handle.update(creature);
      this.handles.push(handle);

      this.add
        .text(x, cellTop + CELL_H - 36, creature.name, {
          fontFamily: 'monospace',
          fontSize: '11px',
          color: '#ffffff',
          align: 'center',
        })
        .setOrigin(0.5)
        .setDepth(500);

      this.add
        .text(x, cellTop + CELL_H - 20, `${entry.bodyLabel} / ${creature.genome.primaryTypes.join('+')}`, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#e6d7c0',
          align: 'center',
        })
        .setOrigin(0.5)
        .setDepth(500);

      this.add
        .text(x, cellTop + CELL_H - 7, `${visual.posture} / ${visual.bodyBias}`, {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: '#9fc8ff',
          align: 'center',
        })
        .setOrigin(0.5)
        .setDepth(500);
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.handles.forEach((h) => h.destroy());
      this.handles = [];
      this.input.off('wheel');
    });
  }
}
